import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectSelectedProducts, selectThread } from '../thread/threadSlice';
import { executeSophiaAction } from '../thread/threadSlice';
import QuadrantChart, { ProductQuadrantData } from '../../components/QuadrantChart';
import { getProductColor } from '../../components/ForecastChart';
import { NO_IMAGE_PLACEHOLDER, normaliseProductDetails, ProductDetails } from '../../utils/product';

interface Prediction {
  date: string;
  quantity: Record<number, number>;
}

interface ForecastResponse {
  shop_id: string;
  product_id: string | null;
  season_id: number;
  forecasts: Prediction[];
}

interface ForecastResponseN {
  shop_id: string;
  product_id_list: string[];
  season_id: number;
  forecasts: ForecastResponse[];
  forecast_reduced: Prediction[] | null;
}

interface SophiaForecastResponse {
  "global-page-data": {
    "forecast_data": ForecastResponseN | null;
    "product_data": Record<string, any>;
    "stores": string[];
    "aggregation_operations": string[];
    "selected_store": string | null;
    "selected_aggregation_operation": string | null;
  };
}

// Global cache to prevent duplicate loads across component remounts
interface CachedForecastData {
  possibleStores: string[];
  forecastData: ForecastResponseN | null;
  selectedStore: string | null;
  productDetails: Record<string, ProductDetails>;
  productSalesData: Record<string, number>;
  productColorMap: Record<string, string>;
  autoHiddenProducts: Set<string>;
  selectedSkus: string[]; // Track which SKUs were used for this cache
}
const forecastCache = new Map<string, CachedForecastData>();
const loadingThreads = new Set<string>();

export function ForecastV2() {
  const dispatch = useAppDispatch();
  const thread = useAppSelector(selectThread);
  const threadId = thread?.threadId;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedSkus = useAppSelector(selectSelectedProducts);
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [possibleStores, setPossibleStores] = useState<string[]>([]);
  const [forecastData, setForecastData] = useState<ForecastResponseN | null>(null);
  const [productDetails, setProductDetails] = useState<Record<string, ProductDetails>>({});
  const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());
  const [autoHiddenProducts, setAutoHiddenProducts] = useState<Set<string>>(new Set());
  const [productSalesData, setProductSalesData] = useState<Record<string, number>>({});
  const [productColorMap, setProductColorMap] = useState<Record<string, string>>({});
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const productRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const latestForecastRequestKey = useRef<string>('');

  const loadPage = useCallback(async () => {
    if (!threadId) return;

    const selectedSkusKey = selectedSkus.join(',');
    const requestKey = `${threadId}:${selectedSkusKey}`;
    latestForecastRequestKey.current = requestKey;

    // Check cache first - but only if selectedSkus haven't changed
    const cached = forecastCache.get(threadId);
    if (cached) {
      // Check if selectedSkus are the same
      const skusMatch = cached.selectedSkus.length === selectedSkus.length &&
                        cached.selectedSkus.every((sku, idx) => sku === selectedSkus[idx]);
      
      if (skusMatch) {
        setPossibleStores(cached.possibleStores);
        setForecastData(cached.forecastData);
        setSelectedStore(cached.selectedStore);
        setProductDetails(cached.productDetails);
        setProductSalesData(cached.productSalesData);
        setProductColorMap(cached.productColorMap);
        setAutoHiddenProducts(new Set());
        setHiddenProducts(new Set());
        return;
      } else {
        // SKUs changed, invalidate cache
        forecastCache.delete(threadId);
      }
    }

    // Check if already loading
    if (loadingThreads.has(requestKey)) {
      return;
    }

    loadingThreads.add(requestKey);
    try {
      setLoading(true);
      
      // Load available shops
      const shopsPayload = await dispatch(
        executeSophiaAction({
          actionName: "list-available-shops",
          params: {},
          refreshThread: false,
        })
      ).unwrap();
      const shopsResponse = shopsPayload.data as { "global-page-data": { "shops_list": string[] } };
      const availableShops = shopsResponse?.["global-page-data"]?.["shops_list"] ?? [];
      const sortedShops = [...availableShops].sort((a, b) => a.localeCompare(b));
      setPossibleStores(sortedShops);
      
      // Load forecast data
      const payload = await dispatch(
        executeSophiaAction({
          actionName: "global-get-page-data",
          params: { 
            "global-page-name": "forecast",
            "visible-products": selectedSkus
          },
          refreshThread: false,
        })
      ).unwrap();
      if (latestForecastRequestKey.current !== requestKey) {
        return;
      }

      const response = payload.data as SophiaForecastResponse;
      const pageData = response?.["global-page-data"];
      setForecastData(pageData?.forecast_data ?? null);
      setSelectedStore(pageData?.selected_store ?? null);
      
      // Process product data
      const productData = pageData?.product_data ?? {};
      const normalizedProducts: Record<string, ProductDetails> = {};
      
      for (const [sku, data] of Object.entries(productData)) {
        normalizedProducts[sku] = normaliseProductDetails(data);
      }
      for (const sku of selectedSkus) {
        if (!normalizedProducts[sku]) {
          normalizedProducts[sku] = normaliseProductDetails({ sku });
        }
      }

      setProductDetails(normalizedProducts);
      
      // Calculate sales data and colors
      const salesData: Record<string, number> = {};
      const colorMap: Record<string, string> = {};
      
      if (pageData?.forecast_data?.forecasts) {
        pageData.forecast_data.forecasts.forEach((forecast: ForecastResponse, index: number) => {
          if (forecast.product_id) {
            salesData[forecast.product_id] = calculateTotalSales(forecast.forecasts);
            colorMap[forecast.product_id] = getProductColor(index);
          }
        });
      }
      
      setProductSalesData(salesData);
      setProductColorMap(colorMap);
      
      // Auto-hide products without cod_stagione = 252
      const skusToAutoHide = new Set<string>();
      setAutoHiddenProducts(skusToAutoHide);
      setHiddenProducts(prev => {
        const newSet = new Set(prev);
        autoHiddenProducts.forEach(sku => newSet.delete(sku));
        return newSet;
      });

      // Cache the data
      forecastCache.set(threadId, {
        possibleStores: sortedShops,
        forecastData: pageData?.forecast_data ?? null,
        selectedStore: pageData?.selected_store ?? null,
        productDetails: normalizedProducts,
        productSalesData: salesData,
        productColorMap: colorMap,
        autoHiddenProducts: skusToAutoHide,
        selectedSkus: [...selectedSkus] // Store a copy of selectedSkus
      });
      
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      if (latestForecastRequestKey.current === requestKey) {
        setLoading(false);
      }
      loadingThreads.delete(requestKey);
    }
  }, [dispatch, selectedSkus, threadId]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const handleStoreChange = async (storeValue: string) => {
    try {
      setLoading(true);
      
      // Invalidate cache to force reload of forecast data
      if (threadId) {
        forecastCache.delete(threadId);
      }
      
      if (storeValue) {
        setSelectedStore(storeValue);
        await dispatch(
          executeSophiaAction({
            actionName: "forecast-set-store",
            params: { "forecast-store": storeValue },
            refreshThread: false,
          })
        ).unwrap();
      } else {
        setSelectedStore(null);
        await dispatch(
          executeSophiaAction({
            actionName: "forecast-unset-store",
            params: {},
            refreshThread: false,
          })
        ).unwrap();
      }
      await loadPage();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProductVisibility = (sku: string) => {
    if (autoHiddenProducts.has(sku)) {
      return;
    }
    
    setHiddenProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  };

  const calculateTotalSales = (forecasts: Prediction[]): number => {
    return forecasts.reduce((total, prediction) => {
      const quantity = prediction.quantity[50] || 0;
      return total + quantity;
    }, 0);
  };

  // Compute sorted products based on sales data
  const sortedSelectedSkus = useMemo(() => {
    if (Object.keys(productSalesData).length === 0) {
      return selectedSkus;
    }

    return [...selectedSkus].sort((a, b) => {
      const salesA = productSalesData[a] || 0;
      const salesB = productSalesData[b] || 0;
      return salesB - salesA;
    });
  }, [selectedSkus, productSalesData]);

  // Prepare data for quadrant chart
  const quadrantData: ProductQuadrantData[] = useMemo(() => {
    if (!selectedStore) return [];

    const productIds = new Set<string>(selectedSkus);
    (forecastData?.forecasts ?? []).forEach((forecast: ForecastResponse) => {
      if (forecast.product_id) {
        productIds.add(forecast.product_id);
      }
    });

    return [...productIds]
      .filter(productId => !hiddenProducts.has(productId))
      .map((productId, index) => {
        const product = productDetails[productId];
        const sales = productSalesData[productId] || 0;
        const prezzo = product?.details?.['prezzo'];
        const cost = typeof prezzo === 'number' ? prezzo : 0;
        const color = productColorMap[productId] || getProductColor(index);
        const imageUrl = product?.images?.[0] ?? NO_IMAGE_PLACEHOLDER;

        return {
          productId,
          sales,
          cost,
          color,
          imageUrl
        };
      });
  }, [forecastData, selectedStore, selectedSkus, hiddenProducts, productDetails, productSalesData, productColorMap]);

  const allProductsAutoHidden = sortedSelectedSkus.length > 0 && 
    sortedSelectedSkus.every(sku => autoHiddenProducts.has(sku));

  return (
    <div className="grow w-full h-full p-4 pt-0">
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-black"></div>
            <p className="text-gray-600 font-medium">Caricamento dati forecast...</p>
          </div>
        </div>
      ) : (
        <div className="w-full h-full">
          <div className="w-full h-full flex flex-col">
            {/* Selettori in alto */}
            <div className="flex flex-row items-start gap-6 mb-2 p-4 pb-2" style={{ position: 'relative', zIndex: 1 }}>
              <div className="flex flex-col gap-2 items-center">
                <span className="text-sm font-semibold text-gray-800 uppercase tracking-wide text-center">
                  NEGOZIO SELEZIONATO
                </span>
                <select
                  value={selectedStore || ""}
                  onChange={event => handleStoreChange(event.target.value)}
                  className="border-2 border-black px-3 py-2 bg-white font-medium w-full text-center"
                  style={{ borderRadius: '0px', width: '260px' }}
                >
                  <option value="">-- scegli --</option>
                  {possibleStores.map((store: string) => (
                    <option key={store.split(' - ')[0]} value={store.split(' - ')[0]}>
                      {store}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Selected Items */}
              <div className="flex flex-col gap-2 flex-1 items-end min-w-0" style={{ position: 'relative', zIndex: 2 }}>
                <span className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                  CAPI SELEZIONATI
                </span>
                <div className="flex flex-nowrap gap-2 w-full overflow-x-auto pb-1" style={{ overflowY: 'visible', position: 'relative' }}>
                  {sortedSelectedSkus.length > 0 ? (
                    sortedSelectedSkus.map((sku: string, index: number) => {
                      const product = productDetails[sku];
                      const primaryImage = product?.images?.[0] ?? NO_IMAGE_PLACEHOLDER;
                      
                      const isHidden = hiddenProducts.has(sku);
                      const isAutoHidden = autoHiddenProducts.has(sku);
                      const isManuallyHidden = isHidden && !isAutoHidden;
                      const isHovered = hoveredProduct === sku;
                      const productColor = isHovered ? productColorMap[sku] : undefined;
                      const borderWidth = productColor ? '3px' : '2px';
                      const borderStyle = productColor ? `${borderWidth} solid ${productColor}` : `${borderWidth} solid black`;
                      
                      return (
                        <div 
                          key={index}
                          ref={(el) => { productRefs.current[sku] = el; }}
                          className={`relative group flex-shrink-0 flex flex-col items-center gap-1 ${isHidden ? 'opacity-50' : ''}`}
                          onMouseEnter={(e) => {
                            setHoveredProduct(sku);
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltipPosition({
                              top: rect.top - 8,
                              left: index === 0 ? rect.left : rect.left + rect.width / 2
                            });
                          }}
                          onMouseLeave={() => {
                            setHoveredProduct(null);
                            setTooltipPosition(null);
                          }}
                          style={{ zIndex: isHovered ? 10000 : 1 }}
                        >
                          <div
                            className="w-20 h-20 flex items-center justify-center overflow-hidden relative"
                            style={{ 
                              borderRadius: '0px',
                              border: isHidden ? '2px solid #9ca3af' : borderStyle
                            }}
                          >
                            {primaryImage ? (
                              <img
                                src={primaryImage}
                                alt={product?.code || sku}
                                className={`w-full h-full object-cover ${isHidden ? 'grayscale' : ''}`}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent && !parent.querySelector('.sku-fallback')) {
                                    const fallbackDiv = document.createElement('div');
                                    fallbackDiv.className = `sku-fallback w-full h-full ${isHidden ? 'bg-gray-300' : 'bg-gray-200'} flex items-center justify-center text-base font-bold ${isHidden ? 'text-gray-500' : 'text-gray-700'}`;
                                    fallbackDiv.textContent = sku.length > 4 ? sku.substring(0, 4) : sku;
                                    parent.appendChild(fallbackDiv);
                                  }
                                }}
                              />
                            ) : (
                              <div className={`w-full h-full ${isHidden ? 'bg-gray-300' : 'bg-gray-200'} flex items-center justify-center text-base font-bold ${isHidden ? 'text-gray-500' : 'text-gray-700'}`}>
                                {sku.length > 4 ? sku.substring(0, 4) : sku}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleToggleProductVisibility(sku)}
                            disabled={isAutoHidden}
                            className={`${isAutoHidden ? 'bg-gray-400 cursor-not-allowed' : isHidden ? 'bg-gray-600 hover:bg-gray-700' : 'bg-black hover:bg-gray-800'} text-white text-xs py-1 font-bold w-20`}
                            style={{ borderRadius: '0px' }}
                          >
                            {isHidden ? 'MOSTRA' : 'NASCONDI'}
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-xs text-gray-500 py-4 px-2">
                      Nessun capo selezionato
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {selectedStore ? (
              <div className="flex-1 w-full min-h-0">
                {allProductsAutoHidden ? (
                  <div className="flex-1 flex items-center justify-center h-full" style={{ marginTop: '120px' }}>
                    <div className="flex flex-col items-center gap-4 max-w-md text-center">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-600 font-medium text-lg">
                        Dati di predizione non disponibili
                      </p>
                      <p className="text-gray-500 text-sm">
                        Per i prodotti selezionati non sono disponibili dati di predizione. Seleziona altri prodotti per visualizzare il forecast.
                      </p>
                    </div>
                  </div>
                ) : quadrantData.length > 0 ? (
                  <div className="w-full h-full">
                    <QuadrantChart 
                      data={quadrantData}
                      onProductHover={setHoveredProduct}
                      hoveredProductId={hoveredProduct}
                    />
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <p className="text-gray-600">Nessun dato disponibile per la visualizzazione.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-gray-600">Seleziona un negozio per visualizzare l'analisi forecast.</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Fixed position tooltip */}
      {hoveredProduct && tooltipPosition && (() => {
        const product = productDetails[hoveredProduct];
        const isAutoHidden = autoHiddenProducts.has(hoveredProduct);
        const isHidden = hiddenProducts.has(hoveredProduct);
        const isManuallyHidden = isHidden && !isAutoHidden;
        const isFirstProduct = sortedSelectedSkus.indexOf(hoveredProduct) === 0;
        
        return (
          <div 
            className="bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none"
            style={{
              position: 'fixed',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: isFirstProduct ? 'translateY(-100%)' : 'translate(-50%, -100%)',
              zIndex: 99999,
              opacity: 1,
              transition: 'opacity 200ms'
            }}
          >
            SKU: {product?.code ? `${product.code}` : hoveredProduct} {isManuallyHidden ? '(nascosto)' : ''}
            {isAutoHidden && (
              <>
                <br />Disattivato: dati di predizione non disponibili
              </>
            )}
            {productSalesData[hoveredProduct] !== undefined && !isAutoHidden && (
              <>
                <br />Vendite previste: {productSalesData[hoveredProduct]}
              </>
            )}
            {(() => {
              const prezzo = product?.details?.['prezzo'];
              return typeof prezzo === 'number' && !isAutoHidden ? (
                <>
                  <br />Costo: €{prezzo.toFixed(2)}
                </>
              ) : null;
            })()}
          </div>
        );
      })()}
    </div>
  );
}

export default ForecastV2;
